// CoinGeckoCache.js - Caching system for CoinGecko API calls
class CoinGeckoCache {
  constructor() {
    this.cache = new Map();
    this.lastRequestTimes = new Map();
    this.defaultCacheDuration = 60 * 1000; // 60 seconds in milliseconds
    this.minRequestInterval = 3000; // 3 seconds minimum between requests for same endpoint
    this.globalRequestDelay = 100; // 100ms delay between any API requests
    this.lastGlobalRequest = 0;
  }

  // Generate cache key from URL and params
  generateCacheKey(url, params = {}) {
    const sortedParams = Object.keys(params)
      .sort()
      .map(key => `${key}=${params[key]}`)
      .join('&');
    return `${url}${sortedParams ? '?' + sortedParams : ''}`;
  }

  // Check if we should make a new request or use cache
  shouldMakeRequest(cacheKey) {
    const now = Date.now();
    
    // Check global rate limiting
    if (now - this.lastGlobalRequest < this.globalRequestDelay) {
      return false;
    }

    // Check endpoint-specific rate limiting
    const lastRequestTime = this.lastRequestTimes.get(cacheKey);
    if (lastRequestTime && now - lastRequestTime < this.minRequestInterval) {
      return false;
    }

    // Check if we have valid cached data
    const cachedData = this.cache.get(cacheKey);
    if (cachedData && now - cachedData.timestamp < this.defaultCacheDuration) {
      return false;
    }

    return true;
  }

  // Get data from cache if available and valid
  getFromCache(cacheKey) {
    const cachedData = this.cache.get(cacheKey);
    if (!cachedData) return null;

    const now = Date.now();
    if (now - cachedData.timestamp > this.defaultCacheDuration) {
      // Cache expired, remove it
      this.cache.delete(cacheKey);
      return null;
    }

    return cachedData.data;
  }

  // Store data in cache
  setCache(cacheKey, data) {
    this.cache.set(cacheKey, {
      data,
      timestamp: Date.now()
    });
    this.lastRequestTimes.set(cacheKey, Date.now());
    this.lastGlobalRequest = Date.now();
  }

  // Wait for rate limit if necessary
  async waitForRateLimit(cacheKey) {
    const now = Date.now();
    
    // Check global rate limiting
    const globalWait = this.globalRequestDelay - (now - this.lastGlobalRequest);
    if (globalWait > 0) {
      await new Promise(resolve => setTimeout(resolve, globalWait));
    }

    // Check endpoint-specific rate limiting
    const lastRequestTime = this.lastRequestTimes.get(cacheKey);
    if (lastRequestTime) {
      const endpointWait = this.minRequestInterval - (now - lastRequestTime);
      if (endpointWait > 0) {
        await new Promise(resolve => setTimeout(resolve, endpointWait));
      }
    }
  }

  // Main method to fetch data with caching
  async fetchWithCache(url, options = {}) {
    const cacheKey = this.generateCacheKey(url, options.params);
    
    // Try to get from cache first
    const cachedData = this.getFromCache(cacheKey);
    if (cachedData) {
      return {
        data: cachedData,
        fromCache: true,
        cacheAge: Date.now() - this.cache.get(cacheKey).timestamp
      };
    }

    // Check if we should make a request
    if (!this.shouldMakeRequest(cacheKey)) {
      // Wait for rate limit and try cache again
      await this.waitForRateLimit(cacheKey);
      const retryCache = this.getFromCache(cacheKey);
      if (retryCache) {
        return {
          data: retryCache,
          fromCache: true,
          cacheAge: Date.now() - this.cache.get(cacheKey).timestamp
        };
      }
    }

    // Wait for rate limit before making request
    await this.waitForRateLimit(cacheKey);

    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          'Accept': 'application/json',
          ...options.headers
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Store in cache
      this.setCache(cacheKey, data);
      
      return {
        data,
        fromCache: false,
        cacheAge: 0
      };

    } catch (error) {
      // Try to return stale cache data if available
      const staleData = this.cache.get(cacheKey);
      if (staleData) {
        return {
          data: staleData.data,
          fromCache: true,
          cacheAge: Date.now() - staleData.timestamp,
          isStale: true,
          error: error.message
        };
      }
      
      throw error;
    }
  }

  // Clear all cache
  clearCache() {
    this.cache.clear();
    this.lastRequestTimes.clear();
  }

  // Clear expired cache entries
  clearExpiredCache() {
    const now = Date.now();
    let clearedCount = 0;
    
    for (const [key, data] of this.cache.entries()) {
      if (now - data.timestamp > this.defaultCacheDuration) {
        this.cache.delete(key);
        clearedCount++;
      }
    }
    
    if (clearedCount > 0) {
      // Cleared expired cache entries
    }
  }

  // Get cache stats
  getCacheStats() {
    const now = Date.now();
    let validEntries = 0;
    let expiredEntries = 0;
    
    for (const data of this.cache.values()) {
      if (now - data.timestamp > this.defaultCacheDuration) {
        expiredEntries++;
      } else {
        validEntries++;
      }
    }
    
    return {
      totalEntries: this.cache.size,
      validEntries,
      expiredEntries,
      lastGlobalRequest: this.lastGlobalRequest,
      cacheHitRate: this.cacheHits / (this.cacheHits + this.cacheMisses) || 0
    };
  }
}

// Create a singleton instance
const coinGeckoCache = new CoinGeckoCache();

// Periodically clean expired cache entries
setInterval(() => {
  coinGeckoCache.clearExpiredCache();
}, 10 * 60 * 1000); // Clean every 10 minutes

export default coinGeckoCache;

// Convenience methods for common CoinGecko endpoints
export const fetchCoinDetails = async (coinId) => {
  const url = `https://api.coingecko.com/api/v3/coins/${coinId}`;
  const params = {
    localization: 'false',
    tickers: 'false',
    market_data: 'true',
    community_data: 'true',
    developer_data: 'false',
    sparkline: 'true'
  };
  
  const queryString = Object.entries(params)
    .map(([key, value]) => `${key}=${value}`)
    .join('&');
    
  return coinGeckoCache.fetchWithCache(`${url}?${queryString}`);
};

export const fetchCoinsList = async (params = {}) => {
  const url = 'https://api.coingecko.com/api/v3/coins/markets';
  const defaultParams = {
    vs_currency: 'usd',
    order: 'market_cap_desc',
    per_page: 250,
    page: 1,
    sparkline: 'false',
    price_change_percentage: '1h,24h,7d',
    ...params
  };
  
  const queryString = Object.entries(defaultParams)
    .map(([key, value]) => `${key}=${value}`)
    .join('&');
    
  return coinGeckoCache.fetchWithCache(`${url}?${queryString}`);
};

export const fetchGlobalData = async () => {
  const url = 'https://api.coingecko.com/api/v3/global';
  return coinGeckoCache.fetchWithCache(url);
};

export const fetchSimplePrice = async (ids, vsCurrencies = 'usd', options = {}) => {
  const url = 'https://api.coingecko.com/api/v3/simple/price';
  const params = {
    ids: Array.isArray(ids) ? ids.join(',') : ids,
    vs_currencies: Array.isArray(vsCurrencies) ? vsCurrencies.join(',') : vsCurrencies,
    ...options
  };
  
  const queryString = Object.entries(params)
    .map(([key, value]) => `${key}=${value}`)
    .join('&');
    
  return coinGeckoCache.fetchWithCache(`${url}?${queryString}`);
};

export const fetchSearchResults = async (query) => {
  const url = 'https://api.coingecko.com/api/v3/search';
  const queryString = `query=${encodeURIComponent(query)}`;
  return coinGeckoCache.fetchWithCache(`${url}?${queryString}`);
};